import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { PCP_CONSENT_TYPE, PCP_DECLINE_TYPE, PCP_TYPE } from '../../config/constants';
import { ActivityItem, Dialog, DialogType, IActivityItemProps, Icon, Link, MessageBar, MessageBarType, Spinner } from '@fluentui/react';
import { fetchAttributionHistory } from '../../api/patients';
import { PatientNote, PatientNotePCPHistory } from '../../api/patients.types';

const AttributionHistoryDialog = ({ patientId, onDismiss }: { patientId: string; onDismiss: () => void }) => {
  const [fetching, setFetching] = useState(false);
  const [history, setHistory] = useState<{ note: PatientNote; contents: PatientNotePCPHistory }[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setFetching(true);
    fetchAttributionHistory(patientId)
      .then(setHistory)
      .catch((error) => setError(error.message))
      .finally(() => setFetching(false));
  }, [patientId]);

  const renderActivity = useCallback((patientNote: PatientNote, contents: PatientNotePCPHistory): JSX.Element => {
    return (
      <>
        <div>{patientNote.createdByDisplayName}</div>
        <div>New Status: {PCP_TYPE.find((status) => status.key === contents.newStatus)?.text}</div>
        <div>Previous Status: {PCP_TYPE.find((status) => status.key === contents.previousStatus)?.text}</div>
        {contents.previousStatus === 'NO_ATTRIBUTION' && contents.newStatus === 'ATTRIBUTED_BY_HEAL' && (
          <div>Consent Type: {PCP_CONSENT_TYPE.find((status) => status.key === contents.consentType)?.text}</div>
        )}
        {(PCP_DECLINE_TYPE.find((status) => status.key === contents.newStatus) && <div>Reason: {contents.comment}</div>) ||
          (contents.pcpSelectionReason && <div>Reason: {contents.pcpSelectionReason}</div>)}
      </>
    );
  }, []);

  const renderHistory = useCallback((): JSX.Element => {
    const activityItems: JSX.Element[] = [];
    history.forEach((patientNote, index) => {
      const props: IActivityItemProps = {
        activityDescription: renderActivity(patientNote.note, patientNote.contents),
        activityIcon: <Icon iconName={'Message'} />,
        comments: [],
        timeStamp: `${new Date(patientNote.note.createdAt).toLocaleTimeString()} - ${new Date(patientNote.note.createdAt).toLocaleDateString()}`,
      };
      activityItems.push(<ActivityItem style={{ marginTop: 10 }} {...props} key={index} />);
    });

    if (error) {
      return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
    } else if (history.length === 0) {
      return <MessageBar messageBarType={MessageBarType.info}>No Attribution History</MessageBar>;
    } else {
      return <div>{activityItems}</div>;
    }
    // Intentional to not have error in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, renderActivity]);

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Attribution History',
      }}
      modalProps={{
        isBlocking: true,
      }}
      minWidth={550}
    >
      <div style={{ maxHeight: 600, overflow: 'auto' }}>
        {fetching && <Spinner />}
        {!fetching && renderHistory()}
      </div>
    </Dialog>
  );
};

export const AttributionHistoryLink = ({ patientId }: { patientId: string }) => {
  const [openDialog, setOpenDialog] = useState(false);

  return (
    <>
      <Link onClick={() => setOpenDialog(true)}>View Attribution History...</Link>
      {openDialog && <AttributionHistoryDialog patientId={patientId} onDismiss={() => setOpenDialog(false)} />}
    </>
  );
};
